// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ExpandDoubleVector } from './DoubleMatrix';
import { MAMode, MODE_MAIN, MODE_SIGNAL } from './IndicatorConstants';
import { IndicatorScriptBase } from './IndicatorScriptBase';
export class IndicatorFunctionsNew {
    static CallMovingFunction(maMode, ProcessedIndicator, Period, index = 0, lineNumber = 0, startWithBarIndex = 0) {
        switch (maMode) {
            case MAMode.SMA:
                return this.SMAFunction(ProcessedIndicator, Period, index, lineNumber);
            case MAMode.EMA:
                return this.EMAFunction(ProcessedIndicator, Period, index, lineNumber, startWithBarIndex);
            case MAMode.SMMA:
                return this.SMMAFunction(ProcessedIndicator, Period, index, lineNumber, startWithBarIndex);
            case MAMode.LWMA:
                return this.LWMAFunction(ProcessedIndicator, Period, index, lineNumber);
            default:
                return this.INVALID_VALUE;
        }
    }
    static SMAFunction(ProcessedIndicator, Period, index = 0, IndicatorLineNumber = undefined) {
        const count = this.Count(ProcessedIndicator) - index;
        if (count < Period)
            return this.INVALID_VALUE;
        let sum = 0;
        for (let i = index; i < index + Period; i++) {
            sum += this.ValueRetrieval(ProcessedIndicator, i, IndicatorLineNumber);
        }
        return sum / Period;
    }
    static EMAFunction(ProcessedIndicator, period, index, lineNumber, startWithBarIndex) {
        const total = this.Count(ProcessedIndicator);
        const effective = total - index;
        if (effective < period + startWithBarIndex)
            return this.INVALID_VALUE;
        const baseIndex = total - (period + startWithBarIndex);
        if (baseIndex < 0)
            return this.INVALID_VALUE;
        const k = 2 / (period + 1);
        let ema = this.SMAFunction(ProcessedIndicator, period, baseIndex, lineNumber);
        if (index === baseIndex)
            return ema;
        for (let i = baseIndex - 1; i >= index; i--) {
            const price = this.ValueRetrieval(ProcessedIndicator, i, lineNumber);
            ema = k * price + (1 - k) * ema;
        }
        return ema;
    }
    static SMMAFunction(ProcessedIndicator, period, index, lineNumber, startWithBarIndex) {
        const total = this.Count(ProcessedIndicator);
        const effective = total - index;
        if (effective < period + startWithBarIndex)
            return this.INVALID_VALUE;
        const baseIndex = total - (period + startWithBarIndex);
        if (baseIndex < 0)
            return this.INVALID_VALUE;
        let smma = this.SMAFunction(ProcessedIndicator, period, baseIndex, lineNumber);
        if (index === baseIndex)
            return smma;
        for (let i = baseIndex - 1; i >= index; i--) {
            const price = this.ValueRetrieval(ProcessedIndicator, i, lineNumber);
            smma = (smma * (period - 1) + price) / period;
        }
        return smma;
    }
    static LWMAFunction(ProcessedIndicator, Period, index = 0, IndicatorLineNumber = undefined) {
        const count = this.Count(ProcessedIndicator) - index;
        if (count < Period)
            return this.INVALID_VALUE;
        let numerator = 0;
        let denominator = 0;
        for (let i = 0; i < Period; i++) {
            const weight = Period - i;
            const v = this.ValueRetrieval(ProcessedIndicator, index + i, IndicatorLineNumber);
            numerator += weight * v;
            denominator += weight;
        }
        return denominator !== 0 ? numerator / denominator : this.INVALID_VALUE;
    }
    static ValueRetrieval(ProcessedIndicator, index, IndicatorLineNumber) {
        var _a;
        let value;
        if (IndicatorLineNumber != null && ProcessedIndicator instanceof IndicatorScriptBase) {
            value = ProcessedIndicator.GetValue(IndicatorLineNumber, index);
        }
        else if (ProcessedIndicator instanceof ExpandDoubleVector) {
            const len = (_a = ProcessedIndicator.Length) !== null && _a !== void 0 ? _a : 0;
            const idx = len - 1 - index;
            if (idx < 0 || idx >= len)
                return this.INVALID_VALUE;
            value = ProcessedIndicator[idx];
        }
        else {
            value = this.INVALID_VALUE;
        }
        return value != null && isFinite(value) ? value : this.INVALID_VALUE;
    }
    static Count(ProcessedIndicator) {
        var _a, _b;
        return ProcessedIndicator instanceof IndicatorScriptBase
            ? ((_a = ProcessedIndicator.Count) !== null && _a !== void 0 ? _a : 0)
            : ((_b = ProcessedIndicator.Length) !== null && _b !== void 0 ? _b : 0);
    }
}
IndicatorFunctionsNew.INVALID_VALUE = 0;
/** @deprecated */
export class IndicatorFunctions {
    static CallMovingFunction(maMode, ProcessedIndicator, Period, PreviousValueIndicatorLineNumber = undefined, IndicatorLineNumber = undefined) {
        switch (maMode) {
            case MAMode.SMA:
                return this.SMAFunction(ProcessedIndicator, Period, IndicatorLineNumber);
            case MAMode.EMA:
                return this.EMAFunction(ProcessedIndicator, Period, PreviousValueIndicatorLineNumber, IndicatorLineNumber);
            case MAMode.SMMA:
                return this.SMMAFunction(ProcessedIndicator, Period, PreviousValueIndicatorLineNumber, IndicatorLineNumber);
            case MAMode.LWMA:
                return this.LWMAFunction(ProcessedIndicator, Period, IndicatorLineNumber);
        }
    }
    static MACDFunction(ProcessedIndicator1, ProcessedIndicator2, IndicatorLineNumber, PeriodFast, PeriodSlow, PeriodSignal, differVector) {
        const ema1 = this.EMAFunction(ProcessedIndicator1, PeriodFast, 1); // Sum of prices
        ProcessedIndicator1[ProcessedIndicator1.Length - 1] = ema1;
        const ema2 = this.EMAFunction(ProcessedIndicator2, PeriodSlow, 1);
        ProcessedIndicator2[ProcessedIndicator2.Length - 1] = ema2;
        differVector[differVector.Length - 1] =
            ProcessedIndicator1[ProcessedIndicator1.Length - 1] - ProcessedIndicator2[ProcessedIndicator2.Length - 1];
        if (IndicatorLineNumber === MODE_MAIN) {
            return differVector[differVector.Length - 1];
        }
        else if (IndicatorLineNumber === MODE_SIGNAL) {
            return this.SMAFunction(differVector, PeriodSignal);
        }
    }
    static AOFunction(ProcessedIndicator, IndicatorLineNumber = undefined) {
        const ma34 = this.SMAFunction(ProcessedIndicator, 34, IndicatorLineNumber); // Sum of prices
        const ma5 = this.SMAFunction(ProcessedIndicator, 5, IndicatorLineNumber);
        return ma5 - ma34;
    }
    static SMAFunction(ProcessedIndicator, Period, IndicatorLineNumber = undefined) {
        const count = this.Count(ProcessedIndicator);
        if (count < Period)
            return this.INVALID_VALUE;
        let summa = 0; // Sum of prices
        for (let i = 0; i < Period; i++) {
            summa += this.ValueRetrieval(ProcessedIndicator, i, IndicatorLineNumber);
        }
        return summa / Period;
    }
    static EMAFunction(ProcessedIndicator, Period, PreviousValueIndicatorIndex, IndicatorLineNumber = undefined) {
        const price = this.ValueRetrieval(ProcessedIndicator, 0, IndicatorLineNumber);
        const k = 2 / (Period + 1);
        const prevEMA = this.ValueRetrieval(ProcessedIndicator, PreviousValueIndicatorIndex, IndicatorLineNumber);
        return prevEMA + k * (price - prevEMA);
    }
    static SMMAFunction(ProcessedIndicator, Period, PreviousValueIndicatorIndex, IndicatorLineNumber = undefined) {
        const price = this.ValueRetrieval(ProcessedIndicator, 0, IndicatorLineNumber);
        const prevSMMA = this.ValueRetrieval(ProcessedIndicator, PreviousValueIndicatorIndex, IndicatorLineNumber);
        return (prevSMMA * (Period - 1) + price) / Period;
    }
    static LWMAFunction(ProcessedIndicator, Period, IndicatorLineNumber = undefined) {
        const count = this.Count(ProcessedIndicator);
        if (count < Period)
            return this.INVALID_VALUE;
        let numerator = 0; // Numerator of the rate
        let denominator = 0; // Denominator of the rate
        for (let i = 0; i < Period; i++) {
            const weight = Period - i;
            numerator += weight * this.ValueRetrieval(ProcessedIndicator, i, IndicatorLineNumber);
            denominator += weight;
        }
        return denominator !== 0 ? numerator / denominator : this.INVALID_VALUE;
    }
    static ValueRetrieval(ProcessedIndicator, index, IndicatorLineNumber) {
        if (IndicatorLineNumber != null && ProcessedIndicator instanceof IndicatorScriptBase) {
            return ProcessedIndicator.GetValue(IndicatorLineNumber, index);
        }
        else if (ProcessedIndicator instanceof ExpandDoubleVector) {
            return ProcessedIndicator[ProcessedIndicator.Length - 1 - index];
        }
    }
    static Count(ProcessedIndicator) {
        var _a, _b;
        if (ProcessedIndicator instanceof IndicatorScriptBase) {
            return (_a = ProcessedIndicator.Count) !== null && _a !== void 0 ? _a : 0;
        }
        else {
            return (_b = ProcessedIndicator.Length) !== null && _b !== void 0 ? _b : 0;
        }
    }
}
IndicatorFunctions.INVALID_VALUE = 0;
